// Score & rank system. Pure logic + a small persistence layer over
// localStorage. The world owns the live `stats` object; this module just
// reads/writes it and computes the final rank at extraction time.

import type { World } from '../world/types.js';

export type Rank = 'S' | 'A' | 'B' | 'C' | 'D';

export type ScoreEvent = 'detected' | 'ko' | 'bodyHidden' | 'decoy' | 'knock';

const STORAGE_PREFIX = 'shadowop.best.';

function storageKey(levelId: string): string {
  return STORAGE_PREFIX + levelId;
}

const RANK_VALUES: Record<Rank, number> = {
  S: 5,
  A: 4,
  B: 3,
  C: 2,
  D: 1,
};

export const RANK_COLORS: Record<Rank, string> = {
  S: '#4afca0',
  A: '#7afcc0',
  B: '#a0e8d0',
  C: '#ffb84a',
  D: '#ff7a4a',
};

const RANK_LABELS: Record<Rank, string> = {
  S: 'GHOST',
  A: 'CLEANER',
  B: 'PRO',
  C: 'SLOPPY',
  D: 'TIMEOUT',
};

export interface RankResult {
  rank: Rank;
  label: string;
  time: number;
  targetTime: number;
  kos: number;
  bodiesHidden: number;
  decoysUsed: number;
  knocksUsed: number;
  detected: boolean;
  totalGuards: number;
}

export interface BestRecord {
  rank: Rank;
  time: number;
  kos: number;
  bodiesHidden: number;
  detected: boolean;
}

/** Mutates world.stats in response to a gameplay event. */
export function recordEvent(world: World, kind: ScoreEvent): void {
  switch (kind) {
    case 'detected':
      world.stats.detected = true;
      break;
    case 'ko':
      world.stats.kos++;
      break;
    case 'bodyHidden':
      world.stats.bodiesHidden++;
      break;
    case 'decoy':
      world.stats.decoysUsed++;
      break;
    case 'knock':
      world.stats.knocksUsed++;
      break;
  }
}

/**
 * Compute the rank for the current run. The order of checks matters:
 *
 *   S Ghost    : never detected, no K.O., faster than target time
 *   A Cleaner  : never detected, every guard K.O.'d AND every body hidden
 *   B Pro      : never detected (any other case)
 *   D Timeout  : detected and slower than 2× target time
 *   C Sloppy   : detected (everything else)
 */
export function computeRank(world: World): RankResult {
  const { stats, time, targetTime, guards } = world;
  const totalGuards = guards.length;

  let rank: Rank;
  if (!stats.detected && stats.kos === 0 && time < targetTime) {
    rank = 'S';
  } else if (
    !stats.detected &&
    totalGuards > 0 &&
    stats.kos === totalGuards &&
    stats.bodiesHidden === totalGuards
  ) {
    rank = 'A';
  } else if (!stats.detected) {
    rank = 'B';
  } else if (time > targetTime * 2) {
    rank = 'D';
  } else {
    rank = 'C';
  }

  return {
    rank,
    label: RANK_LABELS[rank],
    time,
    targetTime,
    kos: stats.kos,
    bodiesHidden: stats.bodiesHidden,
    decoysUsed: stats.decoysUsed,
    knocksUsed: stats.knocksUsed,
    detected: stats.detected,
    totalGuards,
  };
}

/** Format an absolute time in seconds as e.g. "47.2s" or "1m12s". */
export function formatTime(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}m${String(s).padStart(2, '0')}s`;
}

// ===== Persistence =====

interface KVStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

let injectedStorage: KVStorage | null = null;

/** Override the storage used by load/save (for tests). Pass null to revert. */
export function setStorage(s: KVStorage | null): void {
  injectedStorage = s;
}

function getStorage(): KVStorage | null {
  if (injectedStorage) return injectedStorage;
  if (typeof localStorage !== 'undefined') return localStorage;
  return null;
}

export function loadBest(levelId: string): BestRecord | null {
  const s = getStorage();
  if (!s) return null;
  try {
    const raw = s.getItem(storageKey(levelId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BestRecord;
    if (!parsed || typeof parsed.rank !== 'string') return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Returns true if the new result becomes the saved best for that level. */
export function saveBestIfBetter(result: RankResult, levelId: string): boolean {
  const s = getStorage();
  if (!s) return false;
  const current = loadBest(levelId);
  if (!isStrictlyBetter(result, current)) return false;
  const record: BestRecord = {
    rank: result.rank,
    time: result.time,
    kos: result.kos,
    bodiesHidden: result.bodiesHidden,
    detected: result.detected,
  };
  try {
    s.setItem(storageKey(levelId), JSON.stringify(record));
    return true;
  } catch {
    return false;
  }
}

function isStrictlyBetter(result: RankResult, current: BestRecord | null): boolean {
  if (!current) return true;
  const rNew = RANK_VALUES[result.rank];
  const rCur = RANK_VALUES[current.rank];
  if (rNew !== rCur) return rNew > rCur;
  // Same rank → faster wins.
  return result.time < current.time;
}
